import type { ErrorCopy } from '@shared/errors/catalog';

/**
 * The `error_code` values the two vocabulary surfaces meet, in the `CONTENT_`
 * namespace. Codes more than one **feature** meets live in
 * `@shared/errors/catalog.ts`; these are the ones only taxonomy raises.
 *
 * **Never inline a backend code in JSX.** The code is a log key; what reaches
 * the operator is the sentence, and the `X-Request-ID` is what they quote.
 *
 * Two maps rather than one, because the noun is the whole difference: «التصنيف»
 * on the tree and «الوسم» on the table, over the same shared base. A single map
 * would have to say «العنصر» on both and lose the only word the operator was
 * looking for.
 */

/** Codes whose sentence does not name the record kind. */
const shared: Readonly<Record<string, ErrorCopy>> = {
  CONTENT_FORBIDDEN: {
    message: 'تعديل المفردات يحتاج دوراً أوسع. راجع مسؤولاً.',
    tone: 'warning',
  },
  CONTENT_SLUG_CONFLICT: {
    message: 'هذا الاسم مستعمل بالفعل. اختر اسماً غيره.',
    tone: 'warning',
  },
  CONTENT_BAD_INPUT: {
    message: 'تعذّر إتمام الطلب بالبيانات الحالية. أعد فتح النافذة ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  CONTENT_RATE_LIMITED: {
    message: 'تعديلات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  CONTENT_UPSTREAM_FAILED: {
    message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.',
    tone: 'neutral',
  },
  CONTENT_UPSTREAM_TIMEOUT: {
    message: 'تأخّرت الخدمة عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.',
    tone: 'neutral',
  },
};

export const categoryErrors: Readonly<Record<string, ErrorCopy>> = {
  ...shared,
  CONTENT_CATEGORY_NOT_FOUND: {
    message: 'لم يعد هذا التصنيف موجوداً. أعد تحميل الصفحة لقراءة الشجرة كما هي الآن.',
    tone: 'warning',
  },
  /**
   * The refusal the configured deletion behaviour produces.
   *
   * `category_delete` is `block-while-non-empty` in «Configuration, not
   * features»: a category still holding titles or child categories is refused
   * by name, and the operator empties or re-parents first. The owning service
   * declares no code of its own for it, so the sentence rides the general 409
   * — which is also the honest fallback if the refusal never arrives, because
   * «something changed under you» is true of every other conflict on this
   * surface.
   */
  CONTENT_CONFLICT: {
    message:
      'لم يُحذف التصنيف: ما زال تحته عناوين أو تصنيفات فرعية. انقلها أو أفرغه أوّلاً ثمّ أعد المحاولة.',
    tone: 'warning',
  },
};

export const tagErrors: Readonly<Record<string, ErrorCopy>> = {
  ...shared,
  CONTENT_TAG_NOT_FOUND: {
    message: 'لم يعد هذا الوسم موجوداً. أعد تحميل الصفحة لقراءة المفردات كما هي الآن.',
    tone: 'warning',
  },
  CONTENT_CONFLICT: {
    message: 'تغيّرت المفردات بينما كانت الصفحة مفتوحة. أعد التحميل ثمّ أعد المحاولة.',
    tone: 'warning',
  },
};
